import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Zap, Rocket, CheckCircle, Star } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const UpgradePrompt = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Only show for free users who haven't dismissed it in this session
    const isFreeUser = user?.subscriptionPlan === 'FREE' || !user?.subscriptionPlan;
    const wasRecentlyDismissed = sessionStorage.getItem('upgradePromptDismissed');

    if (isFreeUser && !wasRecentlyDismissed && !isDismissed) {
      // Show the popup after 30 seconds of being on the dashboard
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    }
  }, [user, isDismissed]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('upgradePromptDismissed', 'true');
  };

  const handleUpgrade = () => {
    setIsVisible(false);
    navigate('/pricing');
  };

  if (!isVisible) return null;

  const creditsRemaining = user?.credits || 0;
  const isLowCredits = creditsRemaining <= 2;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white relative">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Rocket className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Unlock More Power</h3>
              <p className="text-white/80 text-sm">Upgrade to screen more candidates</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLowCredits && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-500" />
              <span className="text-orange-700 text-sm font-medium">
                You have only {creditsRemaining} credit{creditsRemaining !== 1 ? 's' : ''} left!
              </span>
            </div>
          )}

          <p className="text-gray-600 mb-4">
            You're on the <span className="font-semibold text-gray-800">Free Plan</span> with limited screenings.
            Upgrade to Professional and get:
          </p>

          <ul className="space-y-3 mb-6">
            {[
              '50 screenings per month',
              'Up to 20 resumes per job',
              'Advanced AI matching',
              'Email support'
            ].map((feature, i) => (
              <li key={i} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl mb-6">
            <div>
              <div className="text-sm text-gray-600">Professional Plan</div>
              <div className="text-2xl font-bold text-gray-900">$29<span className="text-sm font-normal text-gray-500">/month</span></div>
            </div>
            <div className="flex items-center gap-1 text-amber-500">
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleDismiss}
              className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition"
            >
              Maybe Later
            </button>
            <button
              onClick={handleUpgrade}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition flex items-center justify-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
